/**
 * AudioSettings Component
 * 
 * Provides UI controls for audio preferences:
 * - Master mute toggle
 * - Volume sliders per category
 * - Ambient sound toggle
 * 
 * Persists settings to localStorage automatically.
 */

import React, { useState } from 'react';
import { useAudio } from '../audio/hooks/useAudio.js';

/**
 * Volume slider component
 */
function VolumeSlider({ label, value, onChange, disabled }) {
  const percentage = Math.round(value * 100);
  
  return (
    <div className="flex items-center gap-3 py-2">
      <label className="w-20 text-sm text-gray-300 capitalize">
        {label}
      </label>
      <input
        type="range"
        min="0"
        max="100"
        value={percentage}
        onChange={(e) => onChange(parseInt(e.target.value, 10) / 100)}
        disabled={disabled}
        className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer 
                   disabled:opacity-50 disabled:cursor-not-allowed
                   accent-amber-500"
        aria-label={`${label} volume`}
      />
      <span className="w-10 text-right text-sm text-gray-400">
        {percentage}%
      </span>
    </div>
  );
}

/**
 * Main Audio Settings Panel
 */
export function AudioSettings({ isOpen, onClose }) {
  const {
    isReady,
    isAvailable,
    isMuted,
    settings,
    setVolume,
    toggleMute,
    playAmbient,
    stopAmbient,
    playUI,
    initializeAudio,
  } = useAudio();

  const [ambientPlaying, setAmbientPlaying] = useState(false);

  // Handle first interaction to enable audio
  const handleEnableAudio = async () => {
    const success = await initializeAudio();
    if (success) {
      playUI('confirm');
    }
  };

  // Toggle ambient
  const handleAmbientToggle = () => {
    if (ambientPlaying) {
      stopAmbient();
      setAmbientPlaying(false);
    } else {
      playAmbient('warRoom');
      setAmbientPlaying(true);
    }
  };

  // Play test sound
  const handleTestSound = () => {
    playUI('click');
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="bg-gray-900 border border-gray-700 rounded-lg p-6 w-96 max-w-full mx-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <SpeakerIcon className="w-5 h-5" />
            Audio Settings
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Close audio settings"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Audio not available warning */}
        {!isAvailable && (
          <div className="bg-yellow-900/30 border border-yellow-700 rounded p-3 mb-4">
            <p className="text-yellow-300 text-sm">
              Web Audio API is not supported in your browser.
            </p>
          </div>
        )}

        {/* Enable audio button (shown before first interaction) */}
        {isAvailable && !isReady && (
          <button
            onClick={handleEnableAudio}
            className="w-full bg-amber-600 hover:bg-amber-500 text-white py-3 px-4 
                     rounded-lg mb-4 transition-colors font-medium"
          >
            🔊 Enable Audio
          </button>
        )}

        {/* Master mute toggle */}
        <div className="flex items-center justify-between py-3 border-b border-gray-700 mb-4">
          <span className="text-white font-medium">Master Audio</span>
          <button
            onClick={toggleMute}
            disabled={!isReady}
            className={`px-4 py-2 rounded-lg transition-colors font-medium
                       ${isMuted 
                         ? 'bg-red-900/50 text-red-300 hover:bg-red-900' 
                         : 'bg-green-900/50 text-green-300 hover:bg-green-900'}
                       disabled:opacity-50 disabled:cursor-not-allowed`}
            aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
          >
            {isMuted ? '🔇 Muted' : '🔊 On'}
          </button>
        </div>

        {/* Volume sliders */}
        <div className="space-y-1 mb-6">
          <VolumeSlider
            label="Master"
            value={settings.master}
            onChange={(v) => setVolume('master', v)}
            disabled={!isReady || isMuted}
          />
          <VolumeSlider
            label="UI"
            value={settings.ui}
            onChange={(v) => setVolume('ui', v)}
            disabled={!isReady || isMuted}
          />
          <VolumeSlider
            label="Events"
            value={settings.events}
            onChange={(v) => setVolume('events', v)}
            disabled={!isReady || isMuted}
          />
          <VolumeSlider
            label="Ambient"
            value={settings.ambient}
            onChange={(v) => setVolume('ambient', v)}
            disabled={!isReady || isMuted}
          />
          <VolumeSlider
            label="Music"
            value={settings.music}
            onChange={(v) => setVolume('music', v)}
            disabled={!isReady || isMuted}
          />
        </div>

        {/* Ambient toggle and test */}
        <div className="flex gap-3">
          <button
            onClick={handleAmbientToggle}
            disabled={!isReady || isMuted}
            className={`flex-1 py-2 px-4 rounded-lg transition-colors text-sm font-medium
                       ${ambientPlaying
                         ? 'bg-amber-700 text-white hover:bg-amber-600'
                         : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}
                       disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {ambientPlaying ? '⏸ Stop Ambient' : '▶ Start Ambient'}
          </button>
          <button
            onClick={handleTestSound}
            disabled={!isReady || isMuted}
            className="py-2 px-4 bg-gray-700 text-gray-300 rounded-lg 
                     hover:bg-gray-600 transition-colors text-sm font-medium
                     disabled:opacity-50 disabled:cursor-not-allowed"
          >
            🔔 Test
          </button>
        </div>

        {/* Keyboard shortcut hint */}
        <p className="text-gray-500 text-xs text-center mt-4">
          Press <kbd className="px-1 py-0.5 bg-gray-700 rounded">M</kbd> to toggle mute
        </p>
      </div>
    </div>
  );
}

/**
 * Compact audio toggle button for navbar/header
 */
export function AudioToggleButton({ onClick }) {
  const { isMuted, isReady, toggleMute, playUI } = useAudio();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      toggleMute();
      if (!isMuted) {
        // Will be muted, no sound
      } else {
        // Will be unmuted, play feedback
        setTimeout(() => playUI('click'), 50);
      }
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`p-2 rounded-lg transition-colors
                 ${isMuted || !isReady
                   ? 'text-gray-500 hover:text-gray-300' 
                   : 'text-amber-400 hover:text-amber-300'}`}
      aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
      title="Audio Settings"
    >
      {isMuted || !isReady ? (
        <SpeakerOffIcon className="w-5 h-5" />
      ) : (
        <SpeakerIcon className="w-5 h-5" />
      )}
    </button>
  );
}

// =====================================================================
// ICONS (inline SVG to avoid dependencies)
// =====================================================================

function SpeakerIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
    </svg>
  );
}

function SpeakerOffIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
    </svg>
  );
}

function CloseIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

export default AudioSettings;
